import type { Element } from 'hast';

export type ElementSubset = Pick<Element, 'properties' | 'tagName' | 'type'>;

/**
 * Extracts class names from a node.
 *
 * Takes the class and className properties of a node and merges them into one
 * array of classes.
 */
export function extractClassNamesFromNode(node: ElementSubset | undefined): string[] {
  if (!node) return [];

  const classes: string[] = [];
  const classString = node?.properties?.class;
  if (typeof classString === 'string') classes.push(...classString.split(/\s+/));

  // Extract from the properties.className string or array.
  const className = node?.properties?.className;
  if (typeof className === 'string') {
    classes.push(...className.split(/\s+/));
  } else if (Array.isArray(className)) {
    for (const classString of className) {
      if (typeof classString === 'string') classes.push(...classString.split(/\s+/));
    }
  }

  // Remove duplicates.
  return [...new Set(classes)];
}

/**
 * Extracts a class name from a node that starts with a given prefix and
 * returns the class name without the prefix or undefined if no matching class
 * is found.
 */
export function extractClassWithPrefix(
  node: ElementSubset | undefined,
  prefix: string
): string | undefined {
  const tokens = extractClassNamesFromNode(node);
  const matchingToken = tokens.find(token => token.startsWith(prefix));
  return matchingToken ? matchingToken.slice(prefix.length) : undefined;
}
